import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, ExternalLink, Facebook, Instagram, Twitter, Check } from 'lucide-react';
import ContactForm from '../components/ui/ContactForm';

const Contact: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Kontakt | Oljarna Krajnc</title>
        <meta 
          name="description" 
          content="Stopite v stik z Oljarno Krajnc. Najdete nas v Središču ob Dravi, lahko pa nas kontaktirate tudi po telefonu ali e-pošti."
        />
      </Helmet>
      
      <section className="pt-32 pb-16 bg-[url('/images/contact-hero-bg.jpg')] bg-cover bg-center relative">
        <div className="absolute inset-0 bg-primary-700 opacity-70"></div>
        <div className="container-wide relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Kontakt
          </h1>
          <div className="w-24 h-1 bg-secondary mx-auto mb-8"></div>
          <p className="text-white text-lg md:text-xl max-w-3xl mx-auto">
            Stopite v stik z nami. Z veseljem vam bomo odgovorili na vprašanja, 
            sprejeli naročila ali vam pomagali z informacijami o naših izdelkih.
          </p>
        </div>
      </section>
      
      <section className="py-16 bg-neutral">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-primary-700 mb-6">
                  Kontaktne informacije
                </h2>
                
                <div className="space-y-6">
                  <ContactItem 
                    icon={<MapPin className="text-primary-600" size={24} />}
                    title="Naslov"
                    content={
                      <>
                        Oljarna Krajnc, d.o.o.<br />
                        Središče ob Dravi 38<br />
                        2277 Središče ob Dravi<br />
                        Slovenija
                      </>
                    }
                  />
                  
                  <ContactItem 
                    icon={<Phone className="text-primary-600" size={24} />}
                    title="Telefon"
                    content={
                      <a 
                        href="tel:+38627433500" 
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                      >
                        +386 (0)2 743 35 00
                      </a>
                    }
                  />
                  
                  <ContactItem 
                    icon={<Mail className="text-primary-600" size={24} />}
                    title="E-pošta"
                    content={
                      <a 
                        href="mailto:info@oljarna-krajnc.si"
                        className="text-primary-600 hover:text-primary-800 transition-colors"
                      >
                        info@oljarna-krajnc.si
                      </a>
                    }
                  />
                  
                  <ContactItem 
                    icon={<Clock className="text-primary-600" size={24} />}
                    title="Delovni čas"
                    content={
                      <>
                        Pon - Pet: 8:00 - 16:00<br />
                        Sob: 8:00 - 12:00<br />
                        Ned: Zaprto
                      </>
                    }
                  />
                </div>
                
                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-primary-700 mb-4">
                    Sledite nam
                  </h3>
                  
                  <div className="flex space-x-4">
                    <SocialButton href="https://facebook.com" label="Facebook" />
                    <SocialButton href="https://instagram.com" label="Instagram" />
                    <SocialButton href="https://twitter.com" label="Twitter" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
      
      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="container-wide">
          <div className="section-title">
            <h2>Kje nas najdete</h2>
            <div className="divider"></div>
            <p>
              Obiščite našo oljarno v Središču ob Dravi, kjer si lahko ogledate
              tradicionalni postopek pridelave bučnega olja.
            </p>
          </div>
          
          <div className="mt-12 bg-neutral p-2 rounded-lg shadow-md">
            {/* Interactive Map */}
            <div className="h-96 rounded-lg overflow-hidden relative">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5739419970285!2d16.267894600000003!3d46.394517300000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476f6a3c2d8b3eb5%3A0x37b5e22f8eea77b7!2zU3JlZGnFocSNZSBvYiBEcmF2aSwgMjI3NyBTcmVkacWhxI1lIG9iIERyYXZp!5e0!3m2!1ssl!2ssi!4v1710488214801!5m2!1ssl!2ssi" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Lokacija Oljarne Krajnc"
              ></iframe>
              
              <a 
                href="https://goo.gl/maps/Z3jYX6C8TQZ2J7Jt6" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="absolute bottom-4 right-4 bg-white text-primary-700 hover:bg-primary-700 hover:text-white transition-colors px-4 py-2 rounded-md shadow-md flex items-center"
              >
                Odpri v Google Maps
                <ExternalLink size={16} className="ml-2" />
              </a>
            </div>
          </div>
          
          {/* Visit Us Section */}
          <div className="mt-16 bg-primary-50 rounded-lg p-8 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-primary-700 mb-4">
                  Obiščite našo oljarno
                </h3>
                <p className="text-accent-700 mb-6">
                  V naši oljarni vam z veseljem predstavimo tradicionalni postopek pridelave 
                  bučnega olja, našo bogato zgodovino in celotno paleto izdelkov. Organiziramo 
                  tudi vodene oglede za skupine.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <span className="flex-shrink-0 bg-primary-100 rounded-full p-1 mr-2">
                      <Check size={16} className="text-primary-700" />
                    </span>
                    Predstavitve postopka pridelave
                  </li>
                  <li className="flex items-center">
                    <span className="flex-shrink-0 bg-primary-100 rounded-full p-1 mr-2">
                      <Check size={16} className="text-primary-700" />
                    </span>
                    Degustacije izdelkov
                  </li>
                  <li className="flex items-center">
                    <span className="flex-shrink-0 bg-primary-100 rounded-full p-1 mr-2">
                      <Check size={16} className="text-primary-700" />
                    </span>
                    Prodaja na lokaciji
                  </li>
                  <li className="flex items-center">
                    <span className="flex-shrink-0 bg-primary-100 rounded-full p-1 mr-2">
                      <Check size={16} className="text-primary-700" />
                    </span>
                    Vodeni ogledi za skupine
                  </li>
                </ul>
              </div>
              
              <div>
                <img 
                  src="/images/oljarna-visit.jpg" 
                  alt="Obisk oljarne" 
                  className="rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Contact item component
const ContactItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}> = ({ icon, title, content }) => {
  return (
    <div className="flex">
      <div className="flex-shrink-0 mr-4">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-gray-800 mb-1">{title}</h4>
        <div className="text-gray-600">{content}</div>
      </div>
    </div>
  );
};

// Social media button component
const SocialButton: React.FC<{
  href: string;
  label: string;
}> = ({ href, label }) => {
  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="bg-primary-100 hover:bg-primary-200 text-primary-700 p-3 rounded-full transition-colors"
      aria-label={label}
    >
      {label === 'Facebook' && <Facebook size={20} />}
      {label === 'Instagram' && <Instagram size={20} />}
      {label === 'Twitter' && <Twitter size={20} />}
    </a>
  );
};

export default Contact;